// Signed API transport for the sales workspace.
//
// AUTH + CONTEXT CONVENTION (header-based, R1 freeze):
// ====================================================
// Every request from the embedded workspace to the runtime API server
// carries three request-level identifiers derived from the HubSpot session:
//
//   Authorization: Bearer <session.token>
//     The signed HubSpot session JWT. The server (middleware/hubspot-session.mjs)
//     verifies the signature against HubSpot's public key for portal
//     245546374 and extracts portalId + userId + email from it.
//
//   X-Customer-Id: <session.context.objectId>
//     The HubSpot record id the rep is viewing. The server rejects the
//     request if the authenticated user does not own or co-own this
//     customer in HubSpot. This is the main cross-tenant check: the token
//     proves who the rep is, the header + ownership check proves which
//     customer they are allowed to touch.
//
//   X-Work-Id: <session.work?.workId>   (optional)
//     Routed work-order id for the current task. Omitted when the rep is
//     not inside a routed work surface. The server uses this for audit
//     attribution and (eventually) to pin the request to one row in the
//     work queue.
//
// Why headers rather than a signed envelope: the token is already a signed
// JWT; adding a second signature over the headers duplicates work. Headers
// give proxies and audit logs a stable scrape surface. The envelope path
// was considered and rejected to keep the request shape idempotent.
//
// This module wraps the existing RuntimeApiClient (./runtimeClient.ts) with
// a token provider + header injector tied to the SessionProvider.

import {
  RuntimeApiClient,
  RuntimeApiError,
  type RequestOptions,
  type RuntimeApiClientOptions,
} from './runtimeClient.js';
import type { HubSpotSession } from '../auth/hubspot-handshake.js';

export const CUSTOMER_HEADER = 'X-Customer-Id';
export const WORK_HEADER = 'X-Work-Id';
export const PORTAL_HEADER = 'X-Portal-Id';
// W24 (2026-10-20): url-bootstrap sessions carry an opaque token that
// the api-server verifier cannot decode. These headers let the server
// attribute the request to the rep without reading the token payload.
// For real signed sessions the server reads user_id + email from the
// JWT and ignores these headers.
//
// W44 (2026-10-21): Pattern A sessions (api/session-exchange.ts,
// `session.sessionExchanged`) send `Authorization: Bearer ws1.<token>`
// and NOTHING else about the rep: the api-server resolves userId, email
// and role from its session row. The two headers below are attached
// ONLY while `session.urlBootstrapped` is set, which an exchanged
// session never is; that is what the "no X-User-* on the wire" test in
// auth/__tests__/session-exchange.test.ts pins.
export const USER_ID_HEADER = 'X-User-Id';
export const USER_EMAIL_HEADER = 'X-User-Email';

// Session accessor: a function the transport calls on every request to pick
// up the current session. The SessionProvider's handshake can update this
// as the rep navigates records, so we must re-read on each request rather
// than cache at construction time.
export type SessionAccessor = () => HubSpotSession | null;

export interface SignedTransportOptions
  extends Omit<RuntimeApiClientOptions, 'getToken'> {
  getSession: SessionAccessor;
}

export interface SignedRequestOptions
  extends Omit<RequestOptions, 'timeoutMs'> {
  // Per-request overrides. In practice callers do not need these; the
  // headers come from the session automatically.
  customerIdOverride?: string;
  workIdOverride?: string | null;
  timeoutMs?: number;
}

// Low-level signed client. Prefer createSignedRuntimeClient() below unless
// you need direct access to the client's request method.
export class SignedRuntimeApiClient extends RuntimeApiClient {
  private readonly getSession: SessionAccessor;

  constructor(options: SignedTransportOptions) {
    super({
      ...options,
      getToken: () => {
        const s = options.getSession();
        return s ? s.token : null;
      },
    });
    this.getSession = options.getSession;
  }

  // Override request to inject the customer/work headers. The parent class
  // builds its own headers; we extend by wrapping fetch so we can see the
  // outbound request. We do this by overriding the public request method,
  // which the parent exposes.
  override async request<T>(options: RequestOptions): Promise<T> {
    const session = this.getSession();
    if (!session) {
      throw new RuntimeApiError({
        kind: 'AUTH_FAILED',
        status: 0,
        message:
          'No HubSpot session is available. Open the Sales Workspace from a ' +
          'HubSpot contact record and retry.',
        errorCode: 'SESSION_UNAVAILABLE',
      });
    }
    const injected: RequestOptions = {
      ...options,
      headers: {
        ...contextHeaders(session),
        ...(options.headers ?? {}),
      },
    };
    return super.request<T>(injected);
  }
}

// Build the per-request context headers from a session.
export function contextHeaders(session: HubSpotSession): Record<string, string> {
  const headers: Record<string, string> = {
    [CUSTOMER_HEADER]: session.context.objectId,
    [PORTAL_HEADER]: String(session.portalId),
  };
  if (session.work) {
    headers[WORK_HEADER] = session.work.workId;
  }
  // W24: when the session is a url-bootstrap fallback the server cannot
  // read the rep identity out of the token. Attach the user id + email
  // the handshake pulled off the URL params so the server can at least
  // name the actor on the audit row. Harmless when a real JWT arrives
  // later; the server reads the token in that case.
  if (session.urlBootstrapped) {
    if (session.userId) headers[USER_ID_HEADER] = session.userId;
    if (session.email) headers[USER_EMAIL_HEADER] = session.email;
  }
  return headers;
}

export function createSignedRuntimeClient(
  options: SignedTransportOptions,
): SignedRuntimeApiClient {
  return new SignedRuntimeApiClient(options);
}
